import { appendEvent } from './dom/eventRenderer';
import { appendMessage, unmarkMessagePending, clearComposer } from './dom/messageRenderer';
import {
  appendAttachmentPreview, appendAttachment, clearAttachmentPreviews, revokeBlobUrls
} from './dom/attachmentRenderer';
import { connectWebSocket, disconnectWebSocket } from './websocketClient';
import { sendMessage } from './apiRoutes';

const MAX_ATTACHMENTS = 5;

let _pendingFiles = [];

function _renderAttachmentPreviews(container) {
  clearAttachmentPreviews(container);
  _pendingFiles.forEach((file, index) => {
    appendAttachmentPreview(container, file, () => {
      _pendingFiles.splice(index, 1);
      _renderAttachmentPreviews(container);
    });
  });
}

function _addFiles(container, fileList) {
  const remaining = MAX_ATTACHMENTS - _pendingFiles.length;
  const newFiles = Array.from(fileList).slice(0, remaining);
  _pendingFiles.push(...newFiles);
  _renderAttachmentPreviews(container);
}

async function _sendMessage(container) {
  const input = container.querySelector('.aws-chatkit-composer-input');
  const text = input.value.trim();
  const attachments = [..._pendingFiles];

  if (!text && attachments.length === 0) return;

  const pendingMessageId = crypto.randomUUID();
  const messageGroup = appendMessage(container, 'customer', text, 'You', pendingMessageId);
  attachments.forEach((file) => appendAttachment(messageGroup, file));

  _pendingFiles = [];
  clearComposer(container);
  clearAttachmentPreviews(container);

  const ok = await sendMessage(container.dataset.awsConnectContactId, text, attachments);
  if (ok) {
    unmarkMessagePending(messageGroup);
  } else {
    appendEvent(container, 'Your message could not be delivered. Please try again.');
  }
}

function _bindComposer(container) {
  const input = container.querySelector('.aws-chatkit-composer-input');
  const sendBtn = container.querySelector('.aws-chatkit-send-btn');
  if (!input || !sendBtn) return;

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 96)}px`;
  });

  sendBtn.addEventListener('click', () => _sendMessage(container));

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      _sendMessage(container);
    }
  });
}

function _bindLightbox(container) {
  const lightbox = container.querySelector('.aws-chatkit-lightbox');
  const lightboxImg = lightbox.querySelector('.aws-chatkit-lightbox-image');
  const downloadBtn = lightbox.querySelector('.aws-chatkit-lightbox-download');
  const closeBtn = lightbox.querySelector('.aws-chatkit-lightbox-close');

  let _lightboxSrc = '';
  const open = (src) => { _lightboxSrc = src; lightboxImg.src = src; lightbox.classList.add('active') };
  const close = () => { lightbox.classList.remove('active'); lightboxImg.src = ''; _lightboxSrc = '' };

  downloadBtn.addEventListener('click', () => { if (_lightboxSrc) window.open(_lightboxSrc, '_blank'); });

  const openOnClick = (e) => {
    if (e.target.closest('.aws-chatkit-attachment-remove')) return;
    const imageCard = e.target.closest('.aws-chatkit-image-card[data-lightbox-src]');
    if (imageCard) open(imageCard.dataset.lightboxSrc);
  };

  container.querySelector('.aws-chatkit-messages').addEventListener('click', openOnClick);
  const previewArea = container.querySelector('.aws-chatkit-attachments-preview');
  if (previewArea) previewArea.addEventListener('click', openOnClick);

  closeBtn.addEventListener('click', close);
  lightbox.addEventListener('click', (e) => { if (e.target === lightbox) close(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('active')) close();
  });
}

function _bindAttachments(container) {
  const attachBtn = container.querySelector('.aws-chatkit-attach-btn');
  const fileInput = container.querySelector('.aws-chatkit-file-input');
  if (!attachBtn || !fileInput) return;

  attachBtn.addEventListener('click', () => {
    if (_pendingFiles.length >= MAX_ATTACHMENTS) return;
    fileInput.click();
  });

  fileInput.addEventListener('change', () => {
    if (fileInput.files.length > 0) {
      _addFiles(container, fileInput.files);
    }
    fileInput.value = '';
  });
}

export function initializeAWSWidget(container, liveAgentData) {
  _pendingFiles = [];
  _bindComposer(container);
  _bindAttachments(container);
  _bindLightbox(container);
  appendEvent(container, 'Connecting to a live agent...');

  if (!liveAgentData) return;

  const { websocket_url: websocketUrl, aws_connect_contact_id: awsConnectContactId } = liveAgentData;
  container.dataset.awsConnectContactId = awsConnectContactId;
  connectWebSocket(container, websocketUrl);
}

export function destroyAWSWidget(container) {
  _pendingFiles = [];
  if (container) revokeBlobUrls(container);
  disconnectWebSocket();
}
